import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Section, Container, SectionTitle, TitleHighlight, SectionDescription } from '../styles';
import { purpleColors, violetColors, slateColors } from '../styles';

type ScreenshotsProps = {};

const getLocalizedJourneysScreenshot = (language: string): string => {
  const base = '/images/Applandingpage/Journeys';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Journeys_DE.jpeg`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Journeys_FR.jpeg`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Journeys_NL.jpeg`;
  }
  // No NL asset in folder; use EN (same for other locales)
  return `${base}/Journeys_EN.jpeg`;
};

const getLocalizedJourneyDetailsScreenshot = (language: string): string => {
  const base = '/images/Applandingpage/Journey%20Details';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Journeydetails_DE.jpeg`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Journeydetails_FR.jpeg`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Journeydetails_NL.jpeg`;
  }
  return `${base}/Journeydetails_EN.jpeg`;
};

const getLocalizedTrainStatsScreenshot = (language: string): string => {
  const base = '/images/Applandingpage/Train%20stats';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/Trainstats1_DE.jpeg`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/Trainstats1_FR.jpeg`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/Trainstats1_NL.jpeg`;
  }
  return `${base}/Trainstats1_EN.jpeg`;
};

const ScreenshotsSection = styled(Section)`
  background: white;
`;

const ScreenshotsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: start;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ScreenshotCard = styled.div<{ offset?: boolean }>`
  margin-top: ${props => props.offset ? '3rem' : '0'};
  
  @media (min-width: 768px) {
    margin-top: ${props => props.offset ? '3rem' : '0'};
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 1.5rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  background: white;
  transition: all 0.5s;
  
  &:hover {
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
    transform: scale(1.05);
  }
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const CardContent = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

const CardHeading = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${slateColors[900]};
  margin-bottom: 0.5rem;
`;

const CardDescription = styled.p`
  color: ${slateColors[600]};
`;

export default function Screenshots({}: ScreenshotsProps) {
  const { t, i18n } = useTranslation();

  const images = [
    { src: getLocalizedJourneysScreenshot(i18n.language), offset: false },
    { src: getLocalizedJourneyDetailsScreenshot(i18n.language), offset: true },
    { src: getLocalizedTrainStatsScreenshot(i18n.language), offset: false },
  ];

  const cardKeys = ['planning', 'tracking', 'insights'];

  return (
    <ScreenshotsSection>
      <Container>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <SectionTitle>
            {t('appLandingPage.screenshots.heading.regular')}
            <TitleHighlight>{t('appLandingPage.screenshots.heading.highlight')}</TitleHighlight>
          </SectionTitle>
          <SectionDescription>{t('appLandingPage.screenshots.description')}</SectionDescription>
        </div>

        <ScreenshotsGrid>
          {images.map((image, index) => {
            const cardKey = cardKeys[index];
            return (
              <ScreenshotCard key={cardKey} offset={image.offset}>
                <ImageWrapper>
                  <img src={image.src} alt={t(`appLandingPage.screenshots.cards.${index}.imageAlt`)} />
                </ImageWrapper>
                <CardContent>
                  <CardHeading>{t(`appLandingPage.screenshots.cards.${index}.heading`)}</CardHeading>
                  <CardDescription>{t(`appLandingPage.screenshots.cards.${index}.description`)}</CardDescription>
                </CardContent>
              </ScreenshotCard>
            );
          })}
        </ScreenshotsGrid>
      </Container>
    </ScreenshotsSection>
  );
}

